/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Flame, Sparkles, Handshake, Link as LinkIcon, TrendingUp, ChevronRight, ExternalLink } from 'lucide-react';
import { CATEGORY_META, initials, timeAgo } from '@/lib/utils-cat';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const TodayHighlights = ({ onOpenPost }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/discover/today');
        setData(r.data);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
           
          console.debug('[TodayHighlights] discover fetch failed', e);
        }
      }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!data) return null;

  const hasContent = (data.trending_posts?.length || 0) + (data.latest_initiatives?.length || 0) + (data.top_resources?.length || 0) > 0;
  if (!hasContent) return null;

  return (
    <Card className="p-4 rounded-2xl bg-gradient-to-br from-primary/5 via-card to-card border-primary/20" data-testid="today-highlights">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="today-highlights-toggle"
      >
        <div className="h-9 w-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-sm leading-tight">{t('todayHighlightsTitle')}</div>
          <div className="text-[11px] text-muted-foreground">{t('todayHighlightsSub')}</div>
        </div>
        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? '' : 'rotate-90'}`} />
      </button>

      {!collapsed && (
        <div className="mt-3 space-y-3">
          {data.trending_posts?.length > 0 && (
            <Group Icon={Flame} title={t('trendingPosts')} color="#DB2777">
              {data.trending_posts.slice(0, 3).map(p => {
                const cm = CATEGORY_META[p.author?.category];
                return (
                  <button
                    key={p.id}
                    onClick={() => onOpenPost ? onOpenPost(p) : navigate(`/post/${p.id}`)}
                    className="w-full flex items-start gap-2.5 p-2 rounded-xl hover:bg-secondary/40 text-left transition-colors"
                    data-testid={`today-post-${p.id}`}
                  >
                    <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5">
                      {p.author?.avatar_base64 && <AvatarImage src={p.author.avatar_base64} />}
                      <AvatarFallback className="text-[10px]" style={{ backgroundColor: cm?.soft, color: cm?.textOnSoft }}>
                        {initials(p.author?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{p.author?.full_name}</div>
                      <div className="text-[13px] leading-snug line-clamp-2">{p.content}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
                        <span>{timeAgo(p.created_at, t)}</span>
                        <span>·</span>
                        <span>{p.like_count} ♥</span>
                        <span>·</span>
                        <span>{p.comment_count} 💬</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </Group>
          )}

          {data.latest_initiatives?.length > 0 && (
            <Group Icon={Handshake} title={t('latestInitiatives')} color="#7C3AED">
              {data.latest_initiatives.slice(0, 3).map(i => (
                <button
                  key={i.id}
                  onClick={() => navigate('/bacheca')}
                  className="w-full flex items-start gap-2.5 p-2 rounded-xl hover:bg-secondary/40 text-left transition-colors"
                  data-testid={`today-initiative-${i.id}`}
                >
                  <div className="h-7 w-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Handshake className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{i.title}</div>
                    <div className="text-[11px] text-muted-foreground line-clamp-1">{i.description}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {timeAgo(i.created_at, t)} · {i.participant_count} {t('participants')}
                    </div>
                  </div>
                </button>
              ))}
            </Group>
          )}

          {data.top_resources?.length > 0 && (
            <Group Icon={TrendingUp} title={t('topResources')} color="var(--wl-primary)">
              {data.top_resources.slice(0, 3).map(r => (
                <a
                  key={r.id}
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-start gap-2.5 p-2 rounded-xl hover:bg-secondary/40 text-left transition-colors"
                  data-testid={`today-resource-${r.id}`}
                >
                  <div className="h-7 w-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <LinkIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                      {r.title}
                      <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    </div>
                    <div className="text-[11px] text-muted-foreground line-clamp-1">{r.description}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {r.upvote_count} ↑ · {r.type}
                    </div>
                  </div>
                </a>
              ))}
            </Group>
          )}
        </div>
      )}
    </Card>
  );
};

const Group = ({ Icon, title, color, children }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1 px-1">
      <Icon className="h-3 w-3" style={{ color }} />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
    </div>
    <div className="space-y-0.5">{children}</div>
  </div>
);

export default TodayHighlights;
